// Pure state shape + reducer for the template create/edit form. No React, no JSX.
//
// Mirrors gs-ui-templates/src/stores/createTemplate.ts field-by-field so the payload builder
// (payload.js) and a later prefill stay a readable translation of their console counterparts.
// The console uses zustand with 21 hand-written per-field setters; that's a library artifact,
// not a design we need here — one useReducer (formReducer) replaces all of them.
//
// Wire-exact string constants (must match gs-intg-services/.../enterprise-template/constants/
// template.enums.ts and dto/components/*.dto.ts) are exported so payload.js/validate.js never
// re-type a literal that could drift from this file.

export const CATEGORY = {
  MARKETING: 'MARKETING',
  UTILITY: 'UTILITY',
  AUTHENTICATION: 'AUTHENTICATION',
};

// UI-only discriminator for which of headerText/headerMedia is active. Distinct from the wire
// `type` field (TEXT|DOCUMENT|IMAGE|VIDEO|GIF|LOCATION|CAROUSEL), which lives in top-level `type`.
export const HEADER_TYPE = {
  NONE: 'NONE',
  TEXT: 'TEXT',
  MEDIA: 'MEDIA',
};

export const MEDIA_TYPE = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  DOCUMENT: 'DOCUMENT',
  LOCATION: 'LOCATION',
  GIF: 'GIF',
};

// Mirrors gs-intg-services' template-media-rules.constant.ts TEMPLATE_MEDIA_RULES (verified
// 2026-09-22). That file lives in a different repo with no build-time import path into this one,
// so the numbers are copied by hand — keep in sync if the backend rule ever changes. Shared by
// sections/Header.js (all four keys) and sections/Carousel.js (IMAGE/VIDEO only, since a carousel
// only ever offers those two) so the rule table lives in exactly one place instead of two.
export const MEDIA_RULES = {
  [MEDIA_TYPE.IMAGE]: {
    accept: ['image/jpeg', 'image/png', 'image/jpg'],
    maxSizeBytes: 5 * 1024 * 1024,
    maxSizeLabel: '5MB',
  },
  [MEDIA_TYPE.VIDEO]: {
    // video/3gp dropped 2026-09-24 — it can never actually work. No browser reports a picked
    // .3gp file with that exact string; the real one is video/3gpp (this app's own web.xml has
    // no <mime-mapping> override for the .3gp extension, so it falls through to Tomcat's bundled
    // default, which maps .3gp -> video/3gpp). Enterprise rejects a real 3GP upload too, even
    // though its own config nominally allows 'video/3gp': ce-api's validateMediaSample resolves
    // the upload's type via request.getServletContext().getMimeType(filename) — the SAME
    // extension-driven Tomcat map — before comparing it against video.mime.types=video/mp4,
    // video/3gp (environments/prod/config.tpl), so that config entry is exactly as unreachable
    // server-side as it is here. gs-intg-services' own mirror of this table
    // (template-media-rules.constant.ts) still lists it — we deliberately don't copy the dead
    // entry forward. MP4-only also matches sections/Header.js's own MEDIA_TYPE_COPY[VIDEO] hint/
    // error copy, which only ever mentioned MP4 to begin with.
    accept: ['video/mp4'],
    maxSizeBytes: 16 * 1024 * 1024,
    maxSizeLabel: '16MB',
  },
  [MEDIA_TYPE.GIF]: {
    accept: ['video/mp4'],
    maxSizeBytes: Math.round(3.5 * 1024 * 1024),
    maxSizeLabel: '3.5MB',
  },
  [MEDIA_TYPE.DOCUMENT]: {
    accept: ['application/pdf'],
    maxSizeBytes: 30 * 1024 * 1024,
    maxSizeLabel: '30MB',
  },
};

// Every shape a buttons[] entry can take, discriminated by `.type`. QUICK_REPLY/OTP/PASSKEY are
// not part of CallToActionButtonType on the wire (they route to quickReplyButtons/otpButtons/
// passkeyButtons instead) but they still live in this one flat list in state — see payload.js's
// bucketButtons for the split.
export const BUTTON_TYPE = {
  QUICK_REPLY: 'QUICK_REPLY',
  URL: 'URL',
  PHONE_NUMBER: 'PHONE_NUMBER',
  COPY_CODE: 'COPY_CODE',
  FLOW: 'FLOW',
  VOICE_CALL: 'VOICE_CALL',
  ORDER_DETAILS: 'ORDER_DETAILS',
  REQUEST_CONTACT_INFO: 'REQUEST_CONTACT_INFO',
  OTP: 'OTP',
  PASSKEY: 'PASSKEY',
};

// Narrower set CarouselCardButtonDto actually allows.
export const CAROUSEL_BUTTON_TYPE = {
  QUICK_REPLY: 'QUICK_REPLY',
  URL: 'URL',
  PHONE_NUMBER: 'PHONE_NUMBER',
  ORDER_DETAILS: 'ORDER_DETAILS',
};

export const URL_TYPE = {
  STATIC: 'STATIC',
  DYNAMIC: 'DYNAMIC',
  DYNAMIC_LINK_TRACK: 'DYNAMIC_LINK_TRACK',
};

// Enterprise ignores any caller url/example for this urlType and fills both itself from the
// account's link-tracking domain (URLButton.java:166-199) — one state-shape check so every
// section/payload/validate site agrees on what counts as a link-tracked button.
export function isLinkTracked(btn) {
  return btn?.urlType?.value === URL_TYPE.DYNAMIC_LINK_TRACK;
}

// Lowercase on the wire (FlowAction enum), unlike every other enum here — verified against
// flow-button.dto.ts.
export const FLOW_ACTION = {
  NAVIGATE: 'navigate',
  DATA_EXCHANGE: 'data_exchange',
};

export const OTP_TYPE = {
  ONE_TAP: 'ONE_TAP',
  COPY_CODE: 'COPY_CODE',
  ZERO_TAP: 'ZERO_TAP',
  NO_BUTTONS: 'NO_BUTTONS',
};

export const CAROUSEL_MEDIA_TYPE = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
};

// category.selectedSubcategory.value takes one of these. AUTOFILL's wire value is deliberately
// 'ONE_TAP' (not 'AUTOFILL') — it doubles as the otp_type payload.js sends for that subcategory,
// same duality as the console's authSubcategoryType.autofill.
export const SUBCATEGORY = {
  CAROUSEL: 'CAROUSEL',
  LTO: 'LTO',
  FLOW: 'FLOW',
  ORDER_DETAILS: 'ORDER_DETAILS',
  CALL_PERMISSION_REQUEST: 'CALL_PERMISSION_REQUEST',
  COPY_CODE: 'COPY_CODE',
  AUTOFILL: 'ONE_TAP',
  ZERO_TAP: 'ZERO_TAP',
  PASSKEY: 'PASSKEY',
};

// Marketing/Utility's "plain custom message" Template Type option. Deliberately NOT a member of
// SUBCATEGORY above: payload.js never sends subcategory on the wire at all (see its own comment),
// so nothing downstream cares what string this is — it only has to match the value
// sections/Category.js's Select renders and prefill.js seeds, and now that both read it from here,
// they can't drift apart the way a duplicated local const in each file could.
export const CUSTOM_MESSAGE_SUBCATEGORY = 'CUSTOM_MESSAGE';

// Marketing opt-out quick reply/footer — Enterprise overwrites BOTH unconditionally whenever
// opt_out_button is true on a MARKETING template (ce-api/whatsappmediaapi's MessageTemplate.java:
// 1575-1577 overwrites the footer, :1679-1682 overwrites the LAST quick reply's text), sourced
// from ReloadableProperties reading environments/prod/config.tpl:217-218. These are copies of
// that prod config, not a live fetch — this repo has no path to read ce-api's runtime config, so
// if that config value is ever changed these two literals silently drift out of sync with it.
export const MARKETING_OPT_OUT_BUTTON_TEXT = 'Stop Promotions';
export const MARKETING_OPT_OUT_FOOTER = 'Not Interested? Tap Stop Promotions';

// The console locks a Copy Code button's displayed label to this fixed string instead of letting
// the user type one (gs-ui-templates/src/containers/Whatsapp/CreateTemplate/config.ts's
// COPY_CODE_TEXT, read into ButtonsCategory.tsx to set the button's value) — ce-api's
// CopyOfferCodeButton.validate() never checks text at all, only `example` (the code), so this
// form mirrors the console's fixed label rather than rendering an editable text field for it.
export const COPY_CODE_BUTTON_LABEL = 'Copy offer code';

// Console's CONTACT_INFO_TEXT (gs-ui-templates CreateTemplate/config.ts:357) — the fixed, locked
// label for a Request Contact Info button, same pattern as COPY_CODE_BUTTON_LABEL above. ce-api's
// RequestContactInfoButtonDto.text is optional and never validated beyond a max-length, but the
// console still always sends this exact string, never a user-typed one.
export const REQUEST_CONTACT_INFO_BUTTON_TEXT = 'Share Contact Info';

// Console-verbatim (gs-ui-templates en.json's button_label_is_auto_generated), shown under an
// RCI row's locked title field.
export const REQUEST_CONTACT_INFO_WARNING = "Button label is auto-generated and shown in the recipient's language. Customization is not supported.";

// Console's own label for this urlType (gs-ui-templates en.json dynamic_link_tracking).
export const URL_TYPE_LINK_TRACK_LABEL = 'Dynamic link tracking';

// Deliberately NOT the console's tooltip ("This will enable link tracking at project level") --
// that's true on the console's CTM lane but false on Enterprise, which never enables it and
// rejects the save instead (URLButton.java:166-199).
export const LINK_TRACKING_NOTE = 'Gupshup generates this link and tracks clicks on it. You enter the destination URL when you send the message. Link tracking must be enabled on your account; if saving fails, contact support to enable it.';

// Defaults for a freshly created Authentication button (BUTTON_TYPE.OTP) — NOT the same button as
// COPY_CODE_BUTTON_LABEL above: that one is the MARKETING/UTILITY "copy offer code" coupon CTA
// (BUTTON_TYPE.COPY_CODE), a different button with its own different fixed label. These two mirror
// the console's own Auth pre-fill instead (gs-ui-templates/src/containers/Whatsapp/CreateTemplate/
// config.ts:390-392's COPY_CODE_LABEL/AUTOFILL_LABEL, applied by a useEffect in
// Auth/ButtonsText.tsx) — left blank, the Copy Code button's text is required and the very first
// submit fails with "Button title is required" before the user has typed anything. Unlike
// COPY_CODE_BUTTON_LABEL, the user CAN still edit these afterwards; they're only the starting value.
export const OTP_COPY_CODE_BUTTON_TEXT = 'Copy code';
export const OTP_AUTOFILL_BUTTON_TEXT = 'Autofill';

// Shared by sections/Variables.js and prefill.js (both previously kept their own copy of this).
// Document order = first-occurrence order scanning left to right. WhatsApp variables are
// sequential ({{1}}, {{2}}, ...) so this coincides with numeric order for any well-formed body;
// a repeated token (the same {{n}} twice) collapses to one entry — it's one sample value either way.
const TOKEN_REGEX = /\{\{\d+\}\}/g;
export function extractTokens(text) {
  if (!text) return [];
  TOKEN_REGEX.lastIndex = 0;
  const seen = new Set();
  const tokens = [];
  let match = TOKEN_REGEX.exec(text);
  while (match) {
    if (!seen.has(match[0])) {
      seen.add(match[0]);
      tokens.push(match[0]);
    }
    match = TOKEN_REGEX.exec(text);
  }
  return tokens;
}

export function field(value = '') {
  return { value, error: '' };
}

function createHeaderText() {
  return { value: '', error: '', variable: [] };
}

// Shared by header.headerMedia (initialFormState/withHeaderExclusion) and carousels.cards[].
// cardMedia (createCarouselCard) — both need the identical blank
// {value, mediaSrc, mediaId, error, file} shape.
function createMediaField() {
  return { value: '', mediaSrc: '', mediaId: '', error: '', file: null };
}

// Factory, not a shared const — two form instances (e.g. create + edit open in the same session)
// must never alias the same nested objects.
export function initialFormState() {
  return {
    templateName: field(),
    category: {
      value: '',
      error: '',
      selectedSubcategory: field(),
    },
    language: field(),
    header: {
      value: HEADER_TYPE.NONE,
      error: '',
      headerText: createHeaderText(),
      headerMedia: createMediaField(),
    },
    body: {
      value: '',
      error: '',
      variable: [],
      initValue: '',
    },
    footer: {
      footerText: field(),
    },
    buttons: [],
    // Marketing-only flag; payload.js ignores it outright for any other category. Deliberately NOT
    // a row in `buttons` — see MARKETING_OPT_OUT_BUTTON_TEXT/MARKETING_OPT_OUT_FOOTER above: the
    // quick reply and footer text it produces are both fixed strings payload.js appends/overwrites
    // at build time, never something the user edits directly.
    optOutButton: false,
    carousels: {
      mediaType: CAROUSEL_MEDIA_TYPE.IMAGE,
      buttonTypes: [],
      cards: [],
    },
    lto: {
      offerTitle: field(),
      isExpiryChecked: false,
    },
    messageContent: {
      isSecurityCheck: false,
      isExpirationTimeChecked: false,
      expirationTime: field(),
    },
    appsSetup: [],
    messageValidity: {
      validityPeriod: field(),
      isValidityChecked: false,
    },
    status: { value: '' },
    type: { value: '' },
  };
}

// Factories for carousel cards / apps-setup rows, exported so callers building "add a row"
// actions (via the generic 'set' on the whole array) get the same shape state.js itself uses.
export function createCarouselCard(cardId) {
  return {
    cardId,
    cardMedia: createMediaField(),
    description: field(),
    variable: [],
    buttons: [],
    error: false,
  };
}

export function createAppSetup(setupId) {
  return {
    setupId,
    packageName: field(),
    signatureHash: field(),
  };
}

let idCounter = 0;

// Unique per call, never derived from array position/length — two rows removed-then-readded (or
// reordered) must never end up sharing an id, which is what formReducer's removeButton/
// updateButton key off of (btn.buttonId === action.buttonId), and the same requirement
// prefill.js / sections/Carousel.js / sections/AuthAppSetup.js each need for cardId/setupId ids.
// One counter shared across every id "kind" — the prefix already keeps button/card/app-setup
// ids from ever colliding, so sharing the counter only makes a collision less likely, never more.
export function createId(prefix) {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

// Extra fields each button TYPE needs, on top of the {buttonId, type, text} every button gets
// below. Field list is read straight off payload.js: CTA_BUILDERS[type] for URL/PHONE_NUMBER/
// COPY_CODE/FLOW/VOICE_CALL/ORDER_DETAILS/REQUEST_CONTACT_INFO, buildOtpButton for OTP,
// bucketButtons for QUICK_REPLY/PASSKEY. Do not add a field no builder there reads — an extra key
// is harmless today but invites drift once payload.js's whitelist assumptions change.
//
// Every field defaults to the empty field()/false shape, with two exceptions: url.urlType
// defaults to STATIC (the common case — a DYNAMIC url needs a suffix too; STATIC is the one that
// works with zero further input), and OTP's text/autofillText default to the console's own
// pre-filled labels (OTP_COPY_CODE_BUTTON_TEXT/OTP_AUTOFILL_BUTTON_TEXT above) rather than blank.
// OTP's `text` here overrides the empty `text: field()` every button starts with below, since
// this factory's return is spread AFTER it in createButton().
const BUTTON_TYPE_EXTRA_FIELDS = {
  [BUTTON_TYPE.URL]: () => ({
    urlType: field(URL_TYPE.STATIC),
    buttonValue: field(),
    suffix: field(),
    hasDeepLink: false,
    deepLink: field(),
    fallbackLink: field(),
  }),
  [BUTTON_TYPE.PHONE_NUMBER]: () => ({
    buttonValue: field(),
  }),
  [BUTTON_TYPE.COPY_CODE]: () => ({
    buttonValue: field(),
  }),
  [BUTTON_TYPE.FLOW]: () => ({
    buttonValue: field(),
    flowId: field(),
    flowAction: field(),
  }),
  [BUTTON_TYPE.VOICE_CALL]: () => ({
    // Console default (gs-ui-templates CreateTemplate/config.ts DEFAULT_ACTIVE_FOR) — a fresh
    // Voice Call button starts at 7 days, not blank. payload.js's own VOICE_CALL builder sends
    // ttl_minutes = days * 1440, so 7 -> 10080, inside Enterprise's accepted 1440-43200 range;
    // validate.js's VOICE_CALL_DAYS_REGEX already accepts 7. prefill.js's reconstructButton
    // always overwrites this with the saved template's own ttl_minutes (or '' if absent) on
    // edit/clone, so this default never leaks into an existing template.
    activeFor: field('7'),
  }),
  [BUTTON_TYPE.ORDER_DETAILS]: () => ({}),
  [BUTTON_TYPE.REQUEST_CONTACT_INFO]: () => ({}),
  [BUTTON_TYPE.QUICK_REPLY]: () => ({}),
  [BUTTON_TYPE.OTP]: () => ({
    text: field(OTP_COPY_CODE_BUTTON_TEXT),
    autofillText: field(OTP_AUTOFILL_BUTTON_TEXT),
    zeroTapTermsAccepted: false,
  }),
  [BUTTON_TYPE.PASSKEY]: () => ({}),
};

// The only button factory — dispatch's addButton must always receive one of these, never a
// hand-built object. Every CTA_BUILDERS/buildOtpButton field reads `.value`/`.error` off a
// {value, error} pair (state.js's own `field()` shape); a caller-supplied plain string or number
// silently vanishes under payload.js's omitEmpty instead of erroring, which is the exact defect
// this factory exists to close. `text` is included for every type (including PASSKEY, whose text
// payload.js ignores and hardcodes to 'Verify' — validate.js's max-length check still reads
// btn.text.value unconditionally for every button, so the field must exist).
//
// `options.locked` marks a row Buttons.js must not offer type-change/remove/drag for — LTO's own
// URL and (when ticked) copy-code CTAs, seeded by Category.js/Lto.js. Defaults false for every
// other caller (addButton, changeButtonType, AuthButtonsText.js's OTP/PASSKEY seed), stored as a real
// key (not omitted when false) so every button carries the same shape.
export function createButton(type, options = {}) {
  const extraFields = BUTTON_TYPE_EXTRA_FIELDS[type];
  return {
    buttonId: createId('button'),
    type,
    text: field(),
    ...(extraFields ? extraFields() : {}),
    locked: Boolean(options.locked),
  };
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Clones every level of `keys` on the way down so each ancestor gets a new reference (React
// change detection) while everything off the path is left untouched. `merge` shallow-merges the
// value object into the leaf node; without it the leaf is replaced outright. Never mutates target.
function setPath(target, keys, value, merge) {
  if (keys.length === 0) {
    if (merge && isPlainObject(value) && isPlainObject(target)) {
      return { ...target, ...value };
    }
    return value;
  }
  const [key, ...rest] = keys;
  const isIndex = /^\d+$/.test(key);
  const container = isIndex
    ? (Array.isArray(target) ? target.slice() : [])
    : (isPlainObject(target) ? { ...target } : {});
  const childKey = isIndex ? Number(key) : key;
  container[childKey] = setPath(container[childKey], rest, value, merge);
  return container;
}

// Mutual exclusion copied from createTemplate.ts:236-283 (updateHeader/updateHeaderText/
// updateHeaderMedia). The bug this prevents: headerText and headerMedia both holding data at
// once, which would send a template with two conflicting header shapes. Rule:
//   - header.value moving away from TEXT resets headerText; away from MEDIA resets headerMedia.
//   - any write into headerText resets headerMedia, and vice versa, unconditionally.
function withHeaderExclusion(nextState, path) {
  if (path === 'header.value') {
    const { value } = nextState.header;
    let header = nextState.header;
    if (value !== HEADER_TYPE.TEXT) {
      header = { ...header, headerText: createHeaderText() };
    }
    if (value !== HEADER_TYPE.MEDIA) {
      header = { ...header, headerMedia: createMediaField() };
    }
    return header === nextState.header ? nextState : { ...nextState, header };
  }
  if (path === 'header.headerText' || path.startsWith('header.headerText.')) {
    return { ...nextState, header: { ...nextState.header, headerMedia: createMediaField() } };
  }
  if (path === 'header.headerMedia' || path.startsWith('header.headerMedia.')) {
    return { ...nextState, header: { ...nextState.header, headerText: createHeaderText() } };
  }
  return nextState;
}

export function formReducer(state, action) {
  switch (action.type) {
    case 'set': {
      const next = setPath(state, action.path.split('.'), action.value, false);
      return withHeaderExclusion(next, action.path);
    }
    case 'merge': {
      const next = setPath(state, action.path.split('.'), action.value, true);
      return withHeaderExclusion(next, action.path);
    }
    case 'reset':
      return action.state;
    case 'addButton':
      return { ...state, buttons: [...state.buttons, action.button] };
    case 'removeButton':
      return { ...state, buttons: state.buttons.filter((btn) => btn.buttonId !== action.buttonId) };
    case 'updateButton':
      return {
        ...state,
        buttons: state.buttons.map((btn) => (
          btn.buttonId === action.buttonId ? { ...btn, ...action.value } : btn
        )),
      };
    default:
      return state;
  }
}

// Named selectors — sections gate on these, never on raw string comparisons (createTemplate.ts:450-579).
export function isMarketing(state) {
  return state.category.value === CATEGORY.MARKETING;
}

export function isUtility(state) {
  return state.category.value === CATEGORY.UTILITY;
}

export function isAuthentication(state) {
  return state.category.value === CATEGORY.AUTHENTICATION;
}

export function isCarousel(state) {
  return state.category.selectedSubcategory.value === SUBCATEGORY.CAROUSEL;
}

export function isLto(state) {
  return state.category.selectedSubcategory.value === SUBCATEGORY.LTO;
}

export function isFlow(state) {
  return state.category.selectedSubcategory.value === SUBCATEGORY.FLOW;
}

export function isOrderDetails(state) {
  return state.category.selectedSubcategory.value === SUBCATEGORY.ORDER_DETAILS;
}

export function isCallPermissionRequest(state) {
  return state.category.selectedSubcategory.value === SUBCATEGORY.CALL_PERMISSION_REQUEST;
}

export function isCopyCode(state) {
  return isAuthentication(state) && state.category.selectedSubcategory.value === SUBCATEGORY.COPY_CODE;
}

export function isAutofill(state) {
  return isAuthentication(state) && state.category.selectedSubcategory.value === SUBCATEGORY.AUTOFILL;
}

export function isZeroTap(state) {
  return isAuthentication(state) && state.category.selectedSubcategory.value === SUBCATEGORY.ZERO_TAP;
}

export function isPasskey(state) {
  return isAuthentication(state) && state.category.selectedSubcategory.value === SUBCATEGORY.PASSKEY;
}

// Request Contact Info is a BUTTON, not a subcategory (added from Buttons.js's own Add CTA menu,
// or by changing a row's type there) — so unlike isCarousel/isLto/etc. above, this reads
// state.buttons directly rather than category.selectedSubcategory. Enterprise requires it be the
// template's only button (ValidationUtils.validateRequestContactInfoTemplate), so "present at
// all" and "present alone" are meant to coincide once Buttons.js's own add/type-change logic has
// run — callers that need to guard against a stale/malformed state should not assume the latter.
export function hasRequestContactInfo(state) {
  return state.buttons.some((btn) => btn.type === BUTTON_TYPE.REQUEST_CONTACT_INFO);
}

// Which button GROUP — quick replies vs. every CTA type — renders/lists first, driven purely by
// state.buttons' own authored order rather than a separate flag: true when the first entry in
// state.buttons is a quick reply, false when it's a CTA. An empty buttons array has no order to
// read yet, so this defaults to true (quick-reply-first), matching the group order this form
// always rendered before per-group ordering existed. Three call sites share this one rule so they
// can never disagree about which group is "first": sections/Buttons.js (which JSX group renders
// on top), Form/index.js's buildPreviewTemplate (the array order the live/preview WhatsApp bubble
// lists buttons in, and — via previewTemplate.containerMeta.buttons — the Preview & Submit
// modal's details-card button line too), and payload.js's computeQuickReplyFirst (whether to send
// the wire's quickReplyFirst flag at all — see that function's own comment for the one extra gate
// it layers on top of this).
export function quickRepliesFirst(state) {
  const first = state.buttons[0];
  return !first || first.type === BUTTON_TYPE.QUICK_REPLY;
}
